// src/pages/Privacy.jsx
import React from "react";
import LegalPageLayout from "./LegalPageLayout";
import LegalDocumentContent from "./LegalDocumentContent";

export default function PrivacyPage() {
  const privacyPolicyText = `GWIN PRIVACY POLICY
Effective date: 1 May 2026
Platform: Gwin
Operator: Marion Rich Pty Ltd, registration number 2022 / 577834 / 07, trading as “Gwin”
Jurisdiction: Republic of South Africa
Contact email: admin@gwin.co.za

1. Introduction
Gwin respects your privacy and is committed to protecting your personal information.
This Privacy Policy explains how Gwin collects, uses, stores, shares, protects, and deletes personal information when you use our website, web application, mobile application, learning platform, dashboards, assessments, templates, scores, subscriptions, partner programmes, and related services.
Gwin is a gamified financial literacy and funding-readiness platform designed to help entrepreneurs improve their financial knowledge, financial habits, business discipline, and readiness for future growth opportunities. Because our platform may process information about you, your business, your learning progress, your financial habits, and your funding-readiness journey, we treat privacy, trust, and responsible data use as core parts of the product.
By using Gwin, creating an account, submitting information, completing lessons, generating scores, using templates, or subscribing to paid features, you agree to the practices described in this Privacy Policy.

2. Important Legal Context
This Privacy Policy is designed for the South African legal environment and is intended to align with the Protection of Personal Information Act, 4 of 2013, commonly known as POPIA.
Where applicable, we may also consider requirements under:
the Electronic Communications and Transactions Act;
the Consumer Protection Act;
the National Credit Act, if future credit-related features are introduced;
financial services laws, if future funder referral or financial product features are introduced;
any other applicable South African law.
This Privacy Policy does not override your legal rights.

3. Who Is Responsible for Your Personal Information?
For purposes of POPIA, Gwin is generally the responsible party for personal information that we collect and process through the Platform.
This means Gwin determines why and how your personal information is processed.
Where we use service providers, they may act as operators who process personal information on our behalf.
Where you access Gwin through a partner, such as an accountant, bookkeeper, coach, incubator, accelerator, ESD programme, sponsor, or funder, that partner may also be a separate responsible party depending on the arrangement.

4. Definitions
In this Privacy Policy:
“Gwin”, “we”, “us”, or “our” means Marion Rich Pty Ltd, trading as Gwin.
“You” or “user” means any person who accesses or uses Gwin.
“Platform” means the Gwin website, application, learning platform, dashboard, scoring system, templates, assessments, tools, and related services.
“Personal information” has the meaning given to it in POPIA and includes information that can identify a natural person or, where applicable, an identifiable juristic person such as a company.
“Processing” means any activity involving personal information, including collection, storage, use, analysis, sharing, updating, deletion, or destruction.
“Score” means any score, readiness indicator, credibility indicator, knowledge score, application score, behaviour score, funding-readiness score, badge, dashboard indicator, or progress measurement generated by Gwin.

5. Personal Information We Collect
We may collect different categories of personal information depending on how you use Gwin.

5.1 Account Information
We may collect:
name and surname;
email address;
mobile number;
username;
password or authentication details;
account preferences;
language preferences;
communication preferences;
account creation date;
login history.

5.2 Identity and Profile Information
We may collect:
ID number or date of birth, if required for verification;
role in the business;
business owner or manager status;
city, province, or area;
user type, such as entrepreneur, accountant, bookkeeper, coach, institutional partner, or programme participant;
onboarding responses;
business stage;
business goals.

5.3 Business Information
We may collect information about your business, including:
business name;
trading name;
company registration number;
CIPC status, where applicable;
business type;
industry;
turnover range;
number of employees;
years in operation;
business location;
business banking status;
VAT registration status, if relevant;
compliance status information that you choose to provide;
business challenges;
funding goals;
growth goals.

5.4 Learning and Progress Information
We may collect:
lessons started and completed;
levels completed;
quiz answers;
quiz scores;
XP points;
streaks;
badges;
practical assignment submissions;
template usage;
unlocks;
certificates;
time spent on lessons;
repeat attempts;
progress through the learning journey;
areas of strength and weakness.

5.5 Scoring and Readiness Information
Gwin may generate or process:
knowledge scores;
application scores;
behaviour scores;
funding-readiness indicators;
financial capability indicators;
progress indicators;
dashboard results;
readiness assessments;
behavioural completion records;
improvement trends;
risk flags or readiness gaps;
recommended next actions.
These scores are used to support learning, behaviour change, progress tracking, and readiness development.
Unless clearly stated otherwise, Gwin scores are not credit scores, loan approvals, legal certifications, tax clearances, or professional financial assessments.

5.6 Financial and Business Behaviour Information
Depending on the features you use, we may collect information about:
financial routines;
cash flow check-ins;
budgeting activities;
debtor tracking activities;
creditor tracking activities;
margin review activities;
pricing exercises;
forecast completion;
funding pack completion;
document readiness;
financial goals;
self-reported business performance;
financial behaviour logs.
At MVP or early stage, much of this may be self-reported. In later versions, some information may be verified through documents, integrations, or third-party data sources, but only where lawful and properly disclosed.

5.7 Documents and Files You Upload
If the Platform allows document upload, we may collect files such as:
financial templates;
completed worksheets;
proof of business registration;
business documents;
financial reports;
cash flow templates;
funding-readiness documents;
compliance documents;
identity or verification documents, where required;
any other document you choose to upload.
You should not upload information that is not requested or that you are not authorised to share.

5.8 Payment and Subscription Information
If you purchase paid access, we may collect:
subscription plan;
billing name;
billing email;
payment status;
payment history;
invoices;
receipts;
VAT/tax invoice information, where applicable;
cancellation status;
refund requests.
We generally do not store full card details ourselves. Payment information may be processed by third-party payment providers.

5.9 Technical and Device Information
We may collect:
IP address;
device type;
browser type;
operating system;
referring pages;
pages viewed;
session duration;
click behaviour;
app events;
crash logs;
error logs;
cookies and similar identifiers;
approximate location based on IP address;
security logs.

5.10 Communications
We may collect information when you communicate with us, including:
support emails;
WhatsApp messages;
chat messages;
survey responses;
feedback;
complaints;
call notes;
meeting notes;
marketing preferences.

5.11 Partner Programme Information
If you access Gwin through a partner programme, we may collect:
programme name;
sponsor or partner name;
cohort details;
attendance or participation data;
completion data;
progress information;
programme reporting data;
consent records relating to information sharing.

6. How We Collect Personal Information
We may collect personal information:
directly from you when you create an account or use the Platform;
when you complete onboarding forms, lessons, quizzes, assessments, or templates;
when you submit documents or practical assignments;
when you subscribe or make payment;
when you contact support;
through cookies, analytics tools, and platform logs;
from partners where you join through a programme;
from service providers who support the Platform;
from third-party integrations, but only where lawful and disclosed;
from public sources, where lawful and relevant.
In future, if Gwin integrates with accounting software, banks, funders, credit bureaus, or verification providers, we will request the appropriate consent and provide additional disclosures where required.

7. Why We Process Your Personal Information

7.1 To Provide the Platform
We use your information to:
create and manage your account;
provide lessons and learning content;
track your progress;
generate scores;
provide dashboards;
unlock features;
issue certificates or badges;
provide templates;
personalise your journey;
save your work;
provide customer support.

7.2 To Measure Learning and Behavioural Progress
We use your information to:
track lesson completion;
measure quiz performance;
identify strengths and weaknesses;
recommend next steps;
support practical application;
assess readiness progress;
show improvement over time.

7.3 To Generate Scores and Readiness Indicators
We use submitted information, activity data, quiz performance, assignment completion, and behavioural indicators to generate Gwin scores and readiness indicators.
These are intended to help you understand where you are, what you need to improve, and how your financial capability is progressing.

7.4 To Improve the Platform
We use information to:
understand user behaviour;
improve lessons;
reduce drop-off;
improve onboarding;
test new features;
improve templates;
improve scoring logic;
monitor platform performance;
identify bugs;
improve user experience.

7.5 To Manage Payments and Subscriptions
We process payment-related information to:
process subscriptions;
issue receipts and invoices;
manage cancellations;
process refunds;
prevent payment abuse;
handle billing queries.

7.6 To Communicate With You
We may communicate with you about:
account activity;
lesson reminders;
product updates;
payment notices;
support responses;
security alerts;
legal updates;
programme messages;
marketing, where permitted.

7.7 To Support Partner or Sponsored Programmes
Where you join Gwin through a partner, sponsor, accountant, bookkeeper, coach, incubator, accelerator, ESD programme, or institutional programme, we may process relevant data to:
administer the programme;
track participation;
measure completion;
provide progress reporting;
support cohort management;
issue certificates;
provide aggregated reporting;
share specific information where you have consented.

7.8 To Protect Gwin and Users
We process information to:
prevent fraud;
detect account sharing;
prevent score manipulation;
investigate false submissions;
protect platform security;
prevent scraping or abuse;
enforce our Terms of Use;
protect intellectual property;
comply with legal obligations.

7.9 To Comply With Law
We may process and retain information where required to:
comply with tax, accounting, consumer protection, privacy, credit, or other laws;
respond to lawful requests;
maintain business records;
investigate disputes;
enforce agreements;
protect legal rights.

8. Legal Bases for Processing
Depending on the situation, we process personal information on one or more lawful grounds, including:
your consent;
performance of a contract with you;
compliance with a legal obligation;
protection of our legitimate interests;
protection of your legitimate interests;
where processing is necessary for the proper performance of a public law duty by a public body, if applicable to a partner programme.
Where we rely on consent, you may withdraw consent, but this may affect your ability to use certain features.

9. Consent
We may ask for your consent separately for certain processing activities, including:
marketing messages;
sharing personal progress with a partner;
sharing scores with an advisor;
sharing readiness information with a funder;
document verification;
accounting software integration;
bank data access;
credit-related checks;
partner referrals;
testimonials or case studies.
Consent must be specific, informed, and voluntary.
You may withdraw consent by contacting us or using available platform settings, but withdrawal may not affect processing that was already lawfully completed.

10. How We Use Scores and Readiness Indicators
Gwin may generate scores to help users understand progress and readiness.
Scores may be based on:
lesson completion;
quiz results;
practical assignments;
self-reported behaviour;
template completion;
frequency of check-ins;
progress trends;
document readiness;
other platform activity.
Gwin scores are primarily for educational, behavioural, and readiness development purposes.
We do not share personal scores with funders, partners, accountants, coaches, or institutions unless:
you have given consent;
the sharing is part of a clearly disclosed programme arrangement;
the data is aggregated or anonymised;
we are legally required to do so.
Gwin may use anonymised or aggregated score data for platform improvement, reporting, research, and impact measurement.

11. Automated Processing and Profiling
Some Gwin features may involve automated processing, including progress tracking, score generation, dashboard indicators, recommendations, and readiness pathways.
These automated outputs are intended to support learning and progress.
They should not be treated as final professional decisions, credit decisions, funding approvals, legal conclusions, tax opinions, or accounting advice.
Where automated processing produces a significant effect on you, and where required by law, we will provide appropriate transparency, consent options, or review mechanisms.

12. Sharing of Personal Information
We do not sell your personal information.
We may share personal information with the following categories of recipients where lawful and necessary.

12.1 Service Providers
We may share information with providers who help us operate Gwin, including:
hosting providers;
payment processors;
email providers;
SMS or WhatsApp providers;
analytics providers;
customer support tools;
database providers;
security providers;
software developers;
professional advisers;
accountants;
legal advisers.
These providers must process information only as authorised and must protect it appropriately.

12.2 Programme Partners
If you join through a partner programme, we may share appropriate participation or progress information with that partner, subject to the programme terms and your consent where required.
Examples of partners include:
accountants;
bookkeepers;
coaches;
incubators;
accelerators;
ESD programmes;
sponsors;
employers;
training providers;
institutional partners.

12.3 Funder or Financial Partners
We will not share your personal funding-readiness information, financial information, scores, documents, credit-related information, bank data, or accounting data with funders or financial partners unless you have consented or another lawful basis applies.
Future funder referral features may require separate consent.

12.4 Legal and Regulatory Sharing
We may share information if required to:
comply with law;
respond to lawful requests;
enforce our Terms;
protect our rights;
investigate fraud;
protect users;
respond to regulators;
comply with court orders.

12.5 Business Transfers
If Gwin is involved in a merger, acquisition, investment, restructuring, sale of assets, or similar transaction, personal information may be shared with relevant parties under appropriate confidentiality protections.

13. Aggregated and Anonymised Data
We may use aggregated or anonymised data for:
product improvement;
market research;
impact reporting;
investor reporting;
partner reporting;
industry insights;
financial capability research;
platform analytics;
public reports.
Aggregated or anonymised data should not identify you personally.
Examples include:
percentage of users completing a module;
average improvement in quiz scores;
common readiness gaps;
industry-level completion rates;
province-level engagement trends;
anonymised funding-readiness trends.

14. Direct Marketing
We may send you marketing communications where permitted by law, including information about:
new features;
premium plans;
partner offers;
events;
funding-readiness resources;
educational content;
programme opportunities.
You may opt out of marketing communications at any time by using the unsubscribe link, changing your preferences, or contacting us.
Even if you opt out of marketing, we may still send necessary service messages, such as account, payment, legal, or security notices.

15. Cookies and Tracking Technologies
Gwin may use cookies, pixels, local storage, analytics tools, and similar technologies to:
keep you logged in;
remember preferences;
secure the Platform;
analyse usage;
improve performance;
personalise content;
understand marketing effectiveness;
prevent fraud.
You can manage cookies through your browser settings. Some features may not work properly if cookies are disabled.
A separate Cookie Policy may provide more detailed information.

16. Data Security
We take reasonable technical and organisational measures to protect personal information against loss, misuse, unauthorised access, disclosure, alteration, or destruction.
Security measures may include:
access controls;
password protection;
encryption where appropriate;
secure hosting;
audit logs;
role-based permissions;
staff and contractor confidentiality obligations;
secure development practices;
backup procedures;
incident response processes.
No system is completely secure. You are responsible for keeping your login details confidential and notifying us if you suspect unauthorised access.

17. Data Retention
We keep personal information only for as long as reasonably necessary for the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law.
Retention periods may depend on:
whether your account is active;
legal recordkeeping requirements;
payment and tax obligations;
dispute resolution needs;
security and fraud prevention;
programme reporting obligations;
contractual obligations;
legitimate business needs.
When information is no longer needed, we will delete, destroy, de-identify, or anonymise it where reasonably possible.

18. Your Rights
Subject to POPIA and applicable law, you may have the right to:
ask whether we hold personal information about you;
request access to your personal information;
request correction of inaccurate, outdated, incomplete, or misleading information;
request deletion of personal information where legally appropriate;
object to certain processing;
withdraw consent where processing is based on consent;
opt out of direct marketing;
complain to the Information Regulator;
request details of third parties who have received your personal information, where applicable.
To exercise your rights, contact us at admin@gwin.co.za.
We may need to verify your identity before responding.

19. Accuracy of Information
You are responsible for ensuring that the information you provide to Gwin is accurate, complete, current, and lawful.
If you provide false, inaccurate, or misleading information, your scores, dashboards, readiness indicators, recommendations, reports, or certificates may be inaccurate.
Gwin is not responsible for decisions made using inaccurate information provided by you.

20. Children’s Privacy
Gwin is not intended for children under the age of 18.
We do not knowingly collect personal information from children.
If we discover that a child has created an account without appropriate legal authority, we may delete or suspend the account.

21. Cross-Border Transfers
Some of our service providers may store or process information outside South Africa.
Where personal information is transferred outside South Africa, we will take reasonable steps to ensure that appropriate safeguards are in place, as required by POPIA.
These safeguards may include contractual protections, security measures, and ensuring that the recipient is subject to privacy protections that are substantially similar to POPIA where required.

22. Third-Party Links
Gwin may contain links to third-party websites, platforms, tools, or services.
We are not responsible for the privacy practices, security, content, or terms of third-party services.
You should read the privacy policies of any third-party services you use.

23. Partner and Sponsored Access
If your access to Gwin is sponsored or provided through a partner, certain information may need to be shared with that partner to manage the programme.
This may include:
registration status;
attendance or participation;
module completion;
certificate status;
aggregated performance;
programme-level reporting.
We will aim to make this clear during onboarding.
More sensitive information, such as detailed scores, uploaded documents, financial information, bank/accounting data, or funding-readiness results, will only be shared where lawful and, where required, with your consent.

24. Funder Referrals and Future Financial Features
Gwin may in future offer funder referral, marketplace, credit-readiness, soft credit check, accounting integration, banking integration, or finance-related features.
Before using those features, you may be asked to accept additional terms and provide additional consent.
These features may involve sharing information with:
funders;
banks;
credit providers;
credit bureaus;
accounting software providers;
bank data providers;
identity verification providers;
document verification providers.
Gwin will not conduct or arrange credit checks, bank data access, accounting software access, or funder data sharing without appropriate disclosure and consent where required.

25. Data Breaches
If we become aware of a security compromise involving personal information, we will take reasonable steps to investigate, contain, and address the issue.
Where required by law, we will notify affected users and/or the Information Regulator.
Notifications may include information about:
what happened;
what information may be affected;
what we are doing;
what you can do to protect yourself;
how to contact us.

26. Complaints
If you believe that we have processed your personal information unlawfully or unfairly, please contact us first so we can try to resolve the issue.
Gwin Privacy Contact:
Email: admin@gwin.co.za
Address: 22 Percy Road, Ottery. Cape Town
You may also lodge a complaint with the South African Information Regulator.

27. Updates to This Privacy Policy
We may update this Privacy Policy from time to time.
If we make material changes, we will take reasonable steps to notify you.
The updated Privacy Policy will apply from the effective date shown at the top of the document.
Your continued use of Gwin after the updated Privacy Policy becomes effective means you accept the updated policy, where acceptance is required by law or contract.

28. Contact Details
For privacy questions, access requests, correction requests, deletion requests, complaints, or consent withdrawals, contact:
Marion Rich Pty Ltd t/a Gwin
Registration number: 2022 / 577834 / 07
Information Officer / Privacy Contact: Candice Shunmoogam
Email: admin@gwin.co.za
Address: 22 Percy Road, Ottery, Cape Town
Website: www.gwin.co.za`;

  return (
    <LegalPageLayout title="Privacy Policy" backTo="/" backLabel="Back">
      <LegalDocumentContent documentText={privacyPolicyText} />
    </LegalPageLayout>
  );
}